import { FormEvent, useCallback, useId, useRef, useState } from "react";
import { FileUp, X } from "lucide-react";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { useNotification } from "../Notification/NotificationProvider";
import { createPolicyCheckVersion } from "../../api/policyCheck";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import {
  formatPolicyCheckFileSize,
  isAcceptedPolicyCheckFile,
  POLICY_CHECK_FILE_ACCEPT,
  POLICY_CHECK_MAX_FILE_BYTES,
} from "../../utils/policyCheckFile";
import "../CreateRequestModal/CreateRequestModal.scss";
import "../CreatePolicyCheckModal/CreatePolicyCheckModal.scss";

interface UploadPolicyVersionModalProps {
  policyCheckRequestId: string;
  onClose: () => void;
  onCreated: (fileId: string) => void;
}

export const UploadPolicyVersionModal = ({
  policyCheckRequestId,
  onClose,
  onCreated,
}: UploadPolicyVersionModalProps) => {
  const { showNotification } = useNotification();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clearFile = useCallback(() => {
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      setFileError(null);
      setGeneralError(null);

      if (!file) {
        setFileError("Выберите файл политики");
        return;
      }

      if (!isAcceptedPolicyCheckFile(file)) {
        setFileError("Допустимы файлы PDF,DOC или DOCX");
        return;
      }

      if (file.size > POLICY_CHECK_MAX_FILE_BYTES) {
        setFileError("Размер файла не должен превышать 2 МБ");
        return;
      }

      setSubmitting(true);
      try {
        const created = await createPolicyCheckVersion(policyCheckRequestId, file);
        const fileId = created.id?.trim();
        if (!fileId) {
          throw new Error("Сервер не вернул идентификатор проверки");
        }
        showNotification({ message: "Новая версия политики загружена", variant: "success" });
        onCreated(fileId);
      } catch (err: unknown) {
        setGeneralError(getApiErrorMessage(err, "Не удалось загрузить новую версию политики"));
      } finally {
        setSubmitting(false);
      }
    },
    [file, onCreated, policyCheckRequestId, showNotification]
  );

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div className="detail-checks-modal" onClick={(e) => e.stopPropagation()}>
          <div className="detail-checks-modal-head">
            <h3>Новая версия политики</h3>
            <button type="button" className="detail-tasklist-close" onClick={onClose} aria-label="Закрыть">
              <X size={18} />
            </button>
          </div>

          <form
            id="upload-policy-version-form"
            className="detail-checks-modal-body create-request-modal-form"
            onSubmit={handleSubmit}
          >
            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor={fileInputId}>
                Файл политики
              </label>
              <input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                accept={POLICY_CHECK_FILE_ACCEPT}
                className="create-policy-check-file-input"
                onChange={(e) => {
                  const nextFile = e.target.files?.[0] ?? null;
                  setFile(nextFile);
                  setFileError(null);
                  setGeneralError(null);
                }}
              />
              <label
                htmlFor={fileInputId}
                className={`create-policy-check-file-btn${file ? " create-policy-check-file-btn--filled" : ""}${
                  fileError ? " create-policy-check-file-btn--error" : ""
                }`}
              >
                <FileUp size={18} className="create-policy-check-file-btn__icon" aria-hidden />
                <span className="create-policy-check-file-btn__text">
                  {file ? file.name : "Загрузить файл (PDF, DOC, DOCX, до 2 МБ)"}
                </span>
                {file ? (
                  <span className="create-policy-check-file-btn__meta">
                    {formatPolicyCheckFileSize(file.size)}
                  </span>
                ) : null}
              </label>
              {file ? (
                <button type="button" className="create-policy-check-file-clear" onClick={clearFile}>
                  Удалить файл
                </button>
              ) : null}
              {fileError ? <span className="create-request-modal-field-error">{fileError}</span> : null}
            </div>

            {generalError ? (
              <span className="create-request-modal-general-error">{generalError}</span>
            ) : null}
          </form>

          <div className="detail-checks-modal-footer">
            <button
              type="submit"
              form="upload-policy-version-form"
              className="create-request-modal-submit"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size="sm" /> : "Запустить проверку"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
